import { clone } from "./utils.js";

/**
 * The key used to store the playlist in window.localStorage.
 * @constant {string}
 */
var PLAYLIST_KEY = "sosPlaylist";

/* Return a unique ID for d
 *
 * The playlist ID provides a unique ID for the dataset in this playlist
 * (useful for setting the ID attribute in the playlist builder UI). A
 * dataset can be placed in a playlist multiple times, so the DataID is not
 * guaranteed to be unique for a given playlist.
 */
function _idFor(d, datasets) {
  var count = 1;
  datasets.forEach(function (ds) {
    if (ds.id === d.id) count++;
  });
  return d.id + "-" + count;
}

/**
 * The default state used to initialize and reset a playlist.
 * @constant {Object}
 * @default
 */
var DEFAULT_STATE = {
  name: "",
  datasets: [],
};

/**
 * State object for the playlist builder.
 *
 * Stores the current playlist's name and the list of datasets in the playlist
 * and notifies subscribers of any changes to that state.
 *
 * @constructor
 */
export default function PlaylistState(name, datasets) {
  this.name = name || DEFAULT_STATE.name.slice();
  this.datasets = datasets || DEFAULT_STATE.datasets.slice();
  this.subscribers = [];
}

/**
 * Return a PlaylistState retrieved from window.localStorage.
 */
PlaylistState.fromLocalStorage = function () {
  var state;

  if (window.playlistStore) return window.playlistStore;

  if (!window.localStorage) {
    console.warn("Local storage not supported. Playlist builder unavailable");
    return;
  }

  state =
    JSON.parse(window.localStorage.getItem(PLAYLIST_KEY)) || DEFAULT_STATE;

  window.playlistStore = new PlaylistState(
    state.name.slice(),
    state.datasets.slice()
  );

  return window.playlistStore;
};

/**
 * Return a frozen object representing the current playlist state.
 */
PlaylistState.prototype.getState = function () {
  return Object.freeze({
    name: this.name,
    datasets: this.datasets,
  });
};

/**
 * Subscribe to changes to the playlist state.
 *
 * @param callback {function} - Executed whenever the state changes, passed
 * the current state as an argument.
 */
PlaylistState.prototype.subscribe = function (callback) {
  this.subscribers.push(callback);
  callback(this.getState());
};

/**
 * Update the state of the playlist.
 *
 * @param {Object} - The new state. If keys are omitted from this object, they
 * will remain unchanged in the state.
 */
PlaylistState.prototype.update = function (state) {
  var newState;

  this.name = typeof state.name === "string" ? state.name : this.name;
  this.datasets = state.datasets || this.datasets;

  window.localStorage.setItem(
    PLAYLIST_KEY,
    JSON.stringify({
      name: this.name,
      datasets: this.datasets,
    })
  );

  newState = this.getState();

  this.subscribers.forEach(function (cb) {
    cb(newState);
  });
};

/**
 * Add a dataset to the playlist
 *
 * @param id {number}
 * @param name {string}
 * @param url {string} - This URL will be linked to from the entry in the
 * playlist builder UI.
 * @param thumbnailBig {string} - URL for the thumbnail of this dataset
 * @param playlistPath {string} - Path to the playlist for this dataset on the sos machine
 */
PlaylistState.prototype.addDataset = function (dataset) {
  var ds = {
    id: dataset.id,
    playlistId: _idFor(dataset, this.datasets),
    name: dataset.name,
    url: dataset.url,
    thumbnailBig: dataset.thumbnailBig,
    selectedVariation: 0,
    variations: [
      { name: dataset.name, playlistPath: dataset.playlistPath },
    ].concat(
      dataset.variations.map(function (v) {
        return { name: v.name, playlistPath: v.playlistPath };
      })
    ),
  };

  var datasets = this.datasets.concat(ds);

  this.update({ datasets: datasets });
};

/**
 * Copy the dataset at idx.
 *
 * A clone of the dataset at idx is created and inserted at idx + 1.
 *
 * @param idx {number} - The index of the dataset that will be cloned.
 */
PlaylistState.prototype.cloneDataset = function (idx) {
  var datasets = this.datasets.slice();
  var copy = clone(datasets[idx]);

  copy.playlistId = _idFor(copy, datasets);
  datasets.splice(idx + 1, 0, copy);
  this.update({ datasets: datasets });
};

/**
 * Move a dataset to a new position in the playlist.
 *
 * @param fromIdx {number} - The index (starting at 0) of the dataset to move
 * @param toIdx {number} - The index (starting at 0) to which the dataset will
 * be moved.
 */
PlaylistState.prototype.moveDataset = function (fromIdx, toIdx) {
  var datasets = this.datasets.slice();

  var ds = datasets.splice(fromIdx, 1);

  datasets.splice(toIdx, 0, ds[0]);

  this.update({ datasets: datasets });
};

/**
 * Remove a dataset from the playlist.
 *
 * @param idx {number} - The index (starting at 0) of the dataset to be
 * removed.
 */
PlaylistState.prototype.removeDataset = function (idx) {
  var datasets = this.datasets.slice();

  datasets.splice(idx, 1);

  this.update({ datasets: datasets });
};

/**
 * Reset the playlist to its default state.
 */
PlaylistState.prototype.resetPlaylist = function () {
  this.update(DEFAULT_STATE);
};

/**
 * Set the name of the playlist.
 *
 * @param name {string}
 */
PlaylistState.prototype.setName = function (name) {
  this.update({ name: name });
};
